import { Inject, Injectable, OnDestroy, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { StockAllocationService } from '../../apiservices/stock-allocation.service';
import { AlertService } from '../alert.service';
import { POSyncProgressEvent, WebSocketService } from './websocket.service';

@Injectable({
  providedIn: 'root'
})
export class POSyncNotificationService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly isBrowser: boolean;
  private initialized = false;

  private readonly ACTIVE_SYNC_JOB_ID_KEY = 'activeSyncJobId';
  private readonly LAST_SYNC_TOAST_JOB_ID_KEY = 'lastSyncToastJobId';

  constructor(
    @Inject(PLATFORM_ID) platformId: Object,
    private webSocketService: WebSocketService,
    private stockAllocationService: StockAllocationService,
    private alertService: AlertService
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  initialize(): void {
    if (!this.isBrowser || this.initialized) return;
    this.initialized = true;

    this.webSocketService.joinRoom('po_sync');
    this.setupWebSocketSubscriptions();
    this.restoreAndPollActiveSync();
  }

  private setupWebSocketSubscriptions(): void {
    this.webSocketService.getPOSyncEvents()
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: POSyncProgressEvent) => {
        const activeJobId = this.getActiveJobId();
        if (!activeJobId || event.jobId !== activeJobId) return;

        if (event.status === 'completed' || event.status === 'failed') {
          this.notifySyncCompletionOnce(event);
          this.clearActiveJob();
        }
      });

    this.webSocketService.getConnectionState()
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        if (state === 'connected') {
          this.webSocketService.joinRoom('po_sync');
          this.restoreAndPollActiveSync();
        }
      });
  }

  private restoreAndPollActiveSync(): void {
    const activeJobId = this.getActiveJobId();
    if (!activeJobId) return;

    this.stockAllocationService.getSyncJobStatus(activeJobId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (job: POSyncProgressEvent) => {
          if (!job) return;
          if (job.status === 'completed' || job.status === 'failed') {
            this.notifySyncCompletionOnce(job);
            this.clearActiveJob();
          }
        },
        error: (err) => {
          // Only a 404 means the job is gone. A transport failure (the browser's "CORS error"
          // when the load balancer answers instead of the API) or a 5xx leaves the job running,
          // so keep its id for the websocket completion event and the next reconnect.
          if (err?.status === 404) {
            this.clearActiveJob();
          }
        }
      });
  }

  private notifySyncCompletionOnce(event: POSyncProgressEvent): void {
    // if (localStorage.getItem(this.LAST_SYNC_TOAST_JOB_ID_KEY) === event.jobId) return;
    const typeLabel = event.type === 'new_po' ? 'New PO' : 'Existing PO';
    const skippedLabel = (count: number) =>
      `${count} ${count === 1 ? 'PO was' : 'POs were'} skipped due to Parked status.`;

    if (event.failed > 0 && event.succeeded > 0) {
      this.alertService.warning(
        `${typeLabel} sync completed: ${event.succeeded} succeeded, ${skippedLabel(event.failed)}`
      );
    } else if (event.failed > 0 && event.succeeded === 0) {
      this.alertService.warning(`${typeLabel} sync completed. ${skippedLabel(event.failed)}`);
    } else {
      this.alertService.success(
        `${typeLabel} sync completed. ${event.succeeded} POs synced.`
      );
    }

    localStorage.setItem(this.LAST_SYNC_TOAST_JOB_ID_KEY, event.jobId);
  }

  private getActiveJobId(): string | null {
    return localStorage.getItem(this.ACTIVE_SYNC_JOB_ID_KEY);
  }

  private clearActiveJob(): void {
    localStorage.removeItem(this.ACTIVE_SYNC_JOB_ID_KEY);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
